/**
 * PrivateVaultLoginForm
 *
 * Private vault credential card for computer-use / chat connector asks.
 * Defaults to username/password (password-manager friendly). When the model
 * already knows the shape (Key ID / Issuer ID / .p8, API tokens, …), it passes
 * custom `fields` — including file uploads. Solid panel UI, not liquid glass.
 */

'use client'

import { useCallback, useMemo, useState } from 'react'
import { Check, KeyRound, Loader2, Paperclip } from 'lucide-react'

import {
  buildComputerUseLoginSecretValue,
  buildComputerUseVaultMetadata,
  buildVaultSecretValueFromFields,
  COMPUTER_USE_VAULT_ALLOWED_SURFACES,
  computerUseVaultCredentialKey,
  computerUseVaultCredentialName,
  inferVaultCredentialKind,
  isDefaultLoginFields,
  normalizeVaultRequestFields,
  readVaultUploadAsText,
  type ComputerUseVaultClient,
  type VaultRequestField
} from '@/lib/computer-use/vault'
import {
  CREW_MEMBER_HEADER,
  crewMemberLabel,
  crewScopedVaultCredentialKey,
  normalizeCrewMemberId
} from '@/lib/crew/member-access'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'

// --------------

export type PrivateVaultLoginFormProps = {
  client: ComputerUseVaultClient
  className?: string
  /** Crew member this login belongs to (own browser + vault). Null = shared. */
  crewMemberId?: string | null
  domain: string
  /**
   * Custom fields from CredentialVault__requestLogin. Omit / empty → classic
   * username + password.
   */
  fields?: VaultRequestField[] | null
  onCancel?: () => void
  onSaved?: (credentialId: string) => void
  reason?: string | null
  sessionId?: string | null
  url: string
}

// --------------

export function hostnameFromVaultUrl(url: string) {
  try {
    return new URL(url.includes('://') ? url : `https://${url}`).hostname.toLowerCase()
  } catch {
    return url.trim().toLowerCase()
  }
}

// --------------

export function PrivateVaultLoginForm({
  client,
  className,
  crewMemberId: rawCrewMemberId = null,
  domain,
  fields: rawFields = null,
  onCancel,
  onSaved,
  reason,
  sessionId,
  url
}: PrivateVaultLoginFormProps) {
  const fields = useMemo(
    () => normalizeVaultRequestFields(rawFields),
    [rawFields]
  )
  const classicLogin = isDefaultLoginFields(fields)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saveToVault, setSaveToVault] = useState(true)
  const [saved, setSaved] = useState(false)
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    for (const field of fields) initial[field.id] = ''
    return initial
  })
  const [fileNames, setFileNames] = useState<Record<string, string>>({})

  const resolvedDomain = (domain || hostnameFromVaultUrl(url)).toLowerCase()
  const crewMemberId = normalizeCrewMemberId(rawCrewMemberId)
  const resolvedReason = reason?.trim() || `Login for ${resolvedDomain}`

  const setFieldValue = useCallback((id: string, value: string) => {
    setValues(prev => ({ ...prev, [id]: value }))
  }, [])

  const handleFileChange = useCallback(
    async (field: VaultRequestField, file: File | null) => {
      if (!file) {
        setFieldValue(field.id, '')
        setFileNames(prev => {
          const next = { ...prev }
          delete next[field.id]
          return next
        })
        return
      }
      try {
        const text = await readVaultUploadAsText(file)
        setFieldValue(field.id, text)
        setFileNames(prev => ({ ...prev, [field.id]: file.name }))
        setError(null)
      } catch (err) {
        setFieldValue(field.id, '')
        setFileNames(prev => {
          const next = { ...prev }
          delete next[field.id]
          return next
        })
        setError(err instanceof Error ? err.message : 'Could not read file')
      }
    },
    [setFieldValue]
  )

  const handleSave = useCallback(async () => {
    for (const field of fields) {
      if (field.required !== false && !values[field.id]?.trim()) {
        setError(
          field.type === 'file'
            ? `Upload ${field.label}`
            : `Enter ${field.label}`
        )
        return
      }
    }
    if (!saveToVault) {
      setError('Saving to your private vault is required')
      return
    }

    setBusy(true)
    setError(null)

    try {
      const kind = inferVaultCredentialKind(fields)
      const secretValue = classicLogin
        ? buildComputerUseLoginSecretValue({
            username: values.username || '',
            password: values.password || ''
          })
        : buildVaultSecretValueFromFields(values)

      const response = await fetch('/api/user-credentials', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          allowed_surfaces: COMPUTER_USE_VAULT_ALLOWED_SURFACES,
          ...(crewMemberId ? { crew_member_id: crewMemberId } : {}),
          description: resolvedReason,
          key: crewScopedVaultCredentialKey(
            computerUseVaultCredentialKey(resolvedDomain),
            crewMemberId
          ),
          kind,
          metadata: {
            ...buildComputerUseVaultMetadata({
              client,
              domain: resolvedDomain,
              url,
              sessionId: sessionId || null
            }),
            field_ids: fields.map(field => field.id),
            field_labels: Object.fromEntries(
              fields.map(field => [field.id, field.label])
            ),
            ...(Object.keys(fileNames).length
              ? { uploaded_files: fileNames }
              : {})
          },
          name: crewMemberId
            ? `${computerUseVaultCredentialName(resolvedDomain)} (${crewMemberLabel(crewMemberId)})`
            : computerUseVaultCredentialName(resolvedDomain),
          provider: resolvedDomain,
          scope: 'personal',
          value: secretValue
        })
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => null)
        throw new Error(payload?.error || 'Failed to save credential')
      }

      const credentialId = (await response.json().catch(() => null))?.credential?.id as
        | string
        | undefined

      // Browser inject only makes sense for classic site logins.
      if (credentialId && url && classicLogin) {
        await fetch('/api/browser/inject-credential', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'content-type': 'application/json',
            ...(crewMemberId ? { [CREW_MEMBER_HEADER]: crewMemberId } : {})
          },
          body: JSON.stringify({ credentialId, targetUrl: url })
        }).catch(() => undefined)
      }

      setValues(prev => {
        const cleared: Record<string, string> = {}
        for (const key of Object.keys(prev)) cleared[key] = ''
        return cleared
      })
      setFileNames({})
      setSaved(true)
      if (credentialId) {
        onSaved?.(credentialId)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save credential')
    } finally {
      setBusy(false)
    }
  }, [
    classicLogin,
    client,
    crewMemberId,
    fields,
    fileNames,
    onSaved,
    resolvedDomain,
    resolvedReason,
    saveToVault,
    sessionId,
    url,
    values
  ])

  if (saved) {
    return (
      <div
        className={cn(
          'my-2 rounded-2xl border border-border bg-card p-4',
          className
        )}
      >
        <div className="flex items-center gap-3">
          <div className="flex size-9 items-center justify-center rounded-xl bg-green-600 text-white">
            <Check className="size-4" strokeWidth={3} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold">Private vault</div>
            <div className="text-xs text-green-500">
              Credentials locked in · invisible to chat
            </div>
          </div>
        </div>
        <Button className="mt-3 w-full" disabled variant="secondary">
          Ready for the agent
        </Button>
      </div>
    )
  }

  return (
    <form
      action={url}
      autoComplete={classicLogin ? 'on' : 'off'}
      className={cn(
        'my-2 space-y-3 rounded-2xl border border-border bg-card p-4',
        className
      )}
      method="post"
      onSubmit={event => {
        event.preventDefault()
        void handleSave()
      }}
    >
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-primary/15 p-2.5">
          <KeyRound className="size-4 text-primary" />
        </div>
        <div className="min-w-0 space-y-1">
          <div className="text-sm font-semibold">Private vault</div>
          <div className="text-xs text-muted-foreground">{resolvedReason}</div>
          <div className="truncate text-xs text-muted-foreground">{url}</div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-background">
        {fields.map((field, index) => {
          const isLast = index === fields.length - 1
          const borderClass = isLast ? '' : 'border-b border-border'

          if (field.type === 'file') {
            const fileName = fileNames[field.id]
            return (
              <label
                key={field.id}
                className={cn(
                  'flex cursor-pointer items-center gap-3 px-3 py-3 text-sm',
                  borderClass,
                  busy && 'pointer-events-none opacity-60'
                )}
              >
                <Paperclip className="size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-muted-foreground">
                    {field.label}
                    {field.required === false ? '' : ' *'}
                  </div>
                  <div className="truncate text-sm">
                    {fileName || field.placeholder || 'Choose file…'}
                  </div>
                </div>
                <input
                  accept={field.accept || '.p8,.pem,.key,text/plain'}
                  className="sr-only"
                  disabled={busy}
                  onChange={event => {
                    void handleFileChange(field, event.target.files?.[0] || null)
                  }}
                  type="file"
                />
              </label>
            )
          }

          const inputType =
            field.type === 'password' || field.type === 'secret'
              ? 'password'
              : field.type === 'email'
                ? 'email'
                : 'text'

          return (
            <Input
              key={field.id}
              autoCapitalize="none"
              autoComplete={
                classicLogin
                  ? field.id === 'username'
                    ? 'username'
                    : field.id === 'password'
                      ? 'current-password'
                      : 'off'
                  : 'off'
              }
              autoCorrect="off"
              className={cn(
                'rounded-none border-0 shadow-none focus-visible:ring-0',
                borderClass
              )}
              disabled={busy}
              id={`private-vault-${field.id}-${resolvedDomain}`}
              inputMode={field.type === 'email' ? 'email' : undefined}
              name={field.id}
              onChange={event => setFieldValue(field.id, event.target.value)}
              placeholder={field.placeholder || field.label}
              spellCheck={false}
              type={inputType}
              value={values[field.id] || ''}
            />
          )
        })}
      </div>

      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={saveToVault}
          onCheckedChange={value => setSaveToVault(value === true)}
        />
        Keep in my private vault
      </label>

      {error ? <div className="text-xs text-destructive">{error}</div> : null}

      <Button className="w-full" disabled={busy} type="submit">
        {busy ? (
          <Loader2 className="size-4 animate-spin" />
        ) : classicLogin ? (
          'Unlock & continue'
        ) : (
          'Save & continue'
        )}
      </Button>
      {onCancel ? (
        <Button
          className="w-full"
          disabled={busy}
          onClick={onCancel}
          type="button"
          variant="ghost"
        >
          Not now
        </Button>
      ) : null}
    </form>
  )
}
